/** @jsxImportSource @emotion/react */
import React from 'react';
import type { AllowedBoundingBox, PlanCell, StructuralObject } from '../../../types/plan';
import type { PlotGridLayers } from '../../../types/bulkOperations';
import type { EntityInteractionState } from '../../../hooks/useEntityInteraction';
import type { EntityLookup } from '../../../utils/planEntities';
import type { PlanLayerVisibility } from '../../../types/planManagement';
import PlanGrid from '../PlanGrid';
import { planEditorStyles } from './planEditorStyles';
import type { BulkPlacementPreview, GridSelectionRect } from './types';

type PlanGridProps = React.ComponentProps<typeof PlanGrid>;

interface PlanGridViewportProps {
  isPlotViewport: boolean;
  entityHintText: string;
  showEntityHint: boolean;
  selectionRect: GridSelectionRect | null;
  appMode: PlanGridProps['appMode'];
  isEraseMode: boolean;
  allowBedSelection: boolean;
  isTransplantTargetMode: boolean;
  planningSelectedCells?: Set<string>;
  bulkPlacementPreview: BulkPlacementPreview | null;
  allowedBoundingBox: AllowedBoundingBox;
  cellMap: Map<string, PlanCell>;
  structuralObjects?: StructuralObject[];
  isSelectedCell: (row: number, col: number) => boolean;
  highlightCells?: Set<string>;
  entityState: EntityInteractionState;
  entityLookup: EntityLookup;
  multiSelectedBeds: Set<string>;
  multiSelectedPlots: Set<string>;
  bedsWithPlantings: Set<string>;
  layerData?: PlotGridLayers;
  layerVisibility?: PlanLayerVisibility;
  onCellMouseDown: (row: number, col: number, e: React.MouseEvent<HTMLDivElement>) => void;
  onCellMouseEnter: (row: number, col: number) => void;
  onCellMouseLeave: () => void;
  onCellMouseUp: () => void;
  onGridMouseLeave: () => void;
  onGridScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  gridRef: (node: HTMLDivElement | null) => void;
}

const PlanGridViewport: React.FC<PlanGridViewportProps> = ({
  isPlotViewport,
  entityHintText,
  showEntityHint,
  selectionRect,
  appMode,
  isEraseMode,
  allowBedSelection,
  isTransplantTargetMode,
  planningSelectedCells,
  bulkPlacementPreview,
  allowedBoundingBox,
  cellMap,
  structuralObjects,
  isSelectedCell,
  highlightCells,
  entityState,
  entityLookup,
  multiSelectedBeds,
  multiSelectedPlots,
  bedsWithPlantings,
  layerData,
  layerVisibility,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseLeave,
  onCellMouseUp,
  onGridMouseLeave,
  onGridScroll,
  gridRef,
}) => {
  return (
    <div
      css={[planEditorStyles.gridWrapper, isPlotViewport && planEditorStyles.plotViewport]}
      ref={gridRef}
      onScroll={onGridScroll}
      onMouseLeave={onGridMouseLeave}
      style={{ position: 'relative' }}
    >
      {showEntityHint && <div css={planEditorStyles.entityHint}>{entityHintText}</div>}
      <PlanGrid
        allowedBoundingBox={allowedBoundingBox}
        cellMap={cellMap}
        structuralObjects={structuralObjects}
        isSelectedCell={isSelectedCell}
        highlightCells={highlightCells}
        selectionRect={selectionRect}
        entityState={entityState}
        entityLookup={entityLookup}
        multiSelectedBeds={multiSelectedBeds}
        multiSelectedPlots={multiSelectedPlots}
        mode={isEraseMode ? 'erase' : 'navigate'}
        appMode={appMode}
        allowBedSelection={allowBedSelection}
        isTransplantTargetMode={isTransplantTargetMode}
        planningSelectedCells={planningSelectedCells}
        bulkPlacementPreview={bulkPlacementPreview}
        bedsWithPlantings={bedsWithPlantings}
        layerData={layerData}
        layerVisibility={layerVisibility}
        onCellMouseDown={onCellMouseDown}
        onCellMouseEnter={onCellMouseEnter}
        onCellMouseLeave={onCellMouseLeave}
        onCellMouseUp={onCellMouseUp}
      />
    </div>
  );
};

export default PlanGridViewport;
